import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, errorMessage } from "@/lib/api";
import { CONTRACT_STATUS, SERVICE_TYPES, UNITS, activeTypeIds, moneyInput, parseMoney } from "@/lib/services";
import { ContractStatusPicker, IntervalPicker } from "@/components/app/ContractPickers";

const F = ({ label, htmlFor, children }) => <div className="space-y-1.5"><Label htmlFor={htmlFor}>{label}</Label>{children}</div>;

const initial = (c, taken) => {
  if (c) return { service_type: c.service_type, price: moneyInput(c.price), own: c.own_interval, interval_value: String(c.effective_interval_value),
    interval_unit: c.effective_interval_unit, status: c.status, notes: c.notes || "" };
  const first = activeTypeIds().find((k) => !taken.includes(k)) || "";
  const t = SERVICE_TYPES[first];
  return { service_type: first, price: moneyInput(t.default_price), own: false, interval_value: String(t.value), interval_unit: t.unit, status: "ativo", notes: "" };
};

export const ContractDialog = ({ state, clientName, onClose, onSaved }) => {
  const editing = state?.contract;
  const taken = state?.taken || [];
  const [f, setF] = useState(initial(null, []));
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (state) setF(initial(editing, taken)); }, [state]); // eslint-disable-line react-hooks/exhaustive-deps

  const pickType = (k) => setF({ ...f, service_type: k, price: moneyInput(SERVICE_TYPES[k].default_price),
    interval_value: String(SERVICE_TYPES[k].value), interval_unit: SERVICE_TYPES[k].unit });

  const run = async (fn, msg) => {
    setSaving(true);
    try { await fn(); toast.success(msg); onSaved(); onClose(); } catch (err) { toast.error(errorMessage(err)); } finally { setSaving(false); }
  };

  const submit = (e) => {
    e.preventDefault();
    const price = parseMoney(f.price);
    if (Number.isNaN(price)) return toast.error("Valor inválido");
    if (!f.service_type) return toast.error("Escolha um serviço");
    const body = { price, status: f.status, notes: f.notes, interval_value: f.own ? Number(f.interval_value) : null, interval_unit: f.own ? f.interval_unit : null };
    return run(() => (editing ? api.put(`/clients/services/${editing.id}`, body)
      : api.post("/clients/services", { ...body, client_name: clientName, service_type: f.service_type })), editing ? "Serviço contratado atualizado." : "Serviço adicionado ao cliente.");
  };

  const remove = () => run(() => api.delete(`/clients/services/${editing.id}`), "Serviço removido da ficha. O histórico foi mantido.");
  const options = Object.entries(SERVICE_TYPES).filter(([k, t]) => (t.active && !taken.includes(k)) || k === f.service_type);
  const t = SERVICE_TYPES[f.service_type];

  return (
    <Dialog open={!!state} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto border-slate-700 bg-slate-900 sm:max-w-lg" data-testid="contract-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold">{editing ? "Editar serviço contratado" : "Adicionar serviço contratado"}</DialogTitle>
          <DialogDescription>{clientName}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <F label="Serviço">
            <Select value={f.service_type} onValueChange={pickType} disabled={!!editing}>
              <SelectTrigger className="h-11 bg-slate-950" data-testid="contract-type-select"><SelectValue placeholder="Todos os serviços já foram adicionados" /></SelectTrigger>
              <SelectContent className="border-slate-700 bg-slate-900">
                {options.map(([k, o]) => (
                  <SelectItem key={k} value={k} data-testid={`contract-type-option-${k}`}>
                    <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: o.hex }} />{o.label}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </F>
          <F label="Valor por atendimento (R$)" htmlFor="ct_price">
            <Input id="ct_price" inputMode="decimal" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} placeholder="Ex.: 150,00" className="h-11 bg-slate-950" data-testid="contract-price-input" />
          </F>
          <IntervalPicker f={f} setF={setF} catalog={t.validity} units={UNITS} />
          <ContractStatusPicker value={f.status} onChange={(status) => setF({ ...f, status })} statuses={CONTRACT_STATUS} />
          <F label="Observações deste serviço" htmlFor="ct_notes">
            <Textarea id="ct_notes" rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="Opcional" className="bg-slate-950" data-testid="contract-notes-input" />
          </F>
          <DialogFooter className="gap-2 sm:justify-between">
            {editing ? <Button type="button" variant="ghost" onClick={remove} disabled={saving} className="text-rose-400 hover:text-rose-300" data-testid="contract-delete-button"><Trash2 className="mr-1 h-4 w-4" /> Remover</Button> : <span />}
            <div className="flex gap-2">
              <Button type="button" variant="ghost" onClick={onClose} data-testid="contract-cancel">Cancelar</Button>
              <Button type="submit" disabled={saving} className="rounded-full font-semibold" data-testid="contract-save">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Salvar
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
